import type {FormDefinitionResponse} from 'src/api/ProactApiClient';
import {FormFieldTypeEnum, FormStateEnum} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import fieldTypeDictionary from 'src/shared/constants/fieldTypeDictionary';
import type {IFormField} from 'src/types/Form';
import {identifierFieldName, identifierFormFieldV2} from 'src/types/FormV2';
import {mapFormFieldSimpleResponseToFormFieldV2} from 'src/types/FormV2Mapping';
import type {IFormFieldV2} from 'src/types/FormV2';

export const getFormPillConfig = (
  state: FormStateEnum,
  identifier?: string
): PillProps[] => {
  const pills = [];
  switch (state) {
    case FormStateEnum.Invalid:
      pills.push({text: 'Invalid', color: 'blue'});
      break;
    case FormStateEnum.Draft:
      pills.push({text: 'Draft', color: 'yellow'});
      break;
    case FormStateEnum.Published:
      pills.push({text: 'Published', color: 'green'});
      break;
    case FormStateEnum.Failed:
      pills.push({text: 'Failed', color: 'red'});
      break;
    default:
      break;
  }

  if (identifier) {
    pills.push({text: identifier, color: 'grey'});
  }

  return pills;
};

export const FormDocumentTitle = 'Forms';

// We insert the Form Description field at the top of all forms. This is later
// mapped directly to the "ShortDescription" property on the Form header.
// We use this data to offer the user some way to describe their submission to their
// administrator.
//
// This is also used in the Safety Event module, too.
export const identifierFormField: IFormField = {
  name: identifierFieldName,
  label: 'Form Description',
  active: true,
  fullWidth: true,

  formFieldTypeGroup: fieldTypeDictionary['single-text'],
  type: 'single-text',
  required: true,
  htmlName: identifierFieldName,
  maxLength: 100,
  listOrder: -1,

  metadata: {
    helpText:
      'Enter a short, one-sentence description (100 characters maxmimum) of your submission. This is used to help identify your submission at a later date.',
  },
};

export function mapFormDefinitionResponseToFormFieldsV2(
  response: FormDefinitionResponse,
  includeBuilderOnlyFields: boolean
): IFormFieldV2[] {
  let fields = response.formFields
    .filter(item =>
      includeBuilderOnlyFields
        ? true
        : item.formFieldType !== FormFieldTypeEnum.Subform
    )
    .slice()
    .sort((a, b) => a.order! - b.order!)
    .map(item => {
      return mapFormFieldSimpleResponseToFormFieldV2(item);
    });

  // If the form identifier field is enabled and the field list is not a for form builder,
  // add the identifier field to the fields array.
  if (
    response.metadata?.enableFormIdentifierField === true &&
    !includeBuilderOnlyFields
  ) {
    fields = [identifierFormFieldV2, ...fields];
  }

  return fields;
}
